/**
 * Created by neo on 11.01.2026
 * Activity Types Configuration - Available activity types for manual logging
 */

export type ActivityMetric = 'duration' | 'distance' | 'calories';

export type ActivityType = {
  id: string;
  name: string;
  icon: string;
  metrics: ActivityMetric[];
  category: 'cardio' | 'strength' | 'flexibility' | 'sports' | 'other';
};

export const ACTIVITY_TYPES: ActivityType[] = [
  // Cardio
  {
    id: 'running',
    name: 'Running',
    icon: '🏃',
    metrics: ['duration', 'distance', 'calories'],
    category: 'cardio',
  },
  {
    id: 'cycling',
    name: 'Cycling',
    icon: '🚴',
    metrics: ['duration', 'distance', 'calories'],
    category: 'cardio',
  },
  {
    id: 'walking',
    name: 'Walking',
    icon: '🚶',
    metrics: ['duration', 'distance', 'calories'],
    category: 'cardio',
  },
  {
    id: 'swimming',
    name: 'Swimming',
    icon: '🏊',
    metrics: ['duration', 'distance', 'calories'],
    category: 'cardio',
  },
  {
    id: 'rowing',
    name: 'Rowing',
    icon: '🚣',
    metrics: ['duration', 'distance', 'calories'],
    category: 'cardio',
  },
  {
    id: 'elliptical',
    name: 'Elliptical',
    icon: '🏃',
    metrics: ['duration', 'calories'],
    category: 'cardio',
  },
  {
    id: 'hiking',
    name: 'Hiking',
    icon: '🥾',
    metrics: ['duration', 'distance', 'calories'],
    category: 'cardio',
  },

  // Strength
  {
    id: 'gym_strength',
    name: 'Strength Training',
    icon: '💪',
    metrics: ['duration', 'calories'],
    category: 'strength',
  },
  {
    id: 'weightlifting',
    name: 'Weightlifting',
    icon: '🏋️',
    metrics: ['duration', 'calories'],
    category: 'strength',
  },
  {
    id: 'bodyweight',
    name: 'Bodyweight Exercises',
    icon: '🤸',
    metrics: ['duration', 'calories'],
    category: 'strength',
  },
  {
    id: 'crossfit',
    name: 'CrossFit',
    icon: '🏋️',
    metrics: ['duration', 'calories'],
    category: 'strength',
  },

  // Flexibility
  {
    id: 'yoga',
    name: 'Yoga',
    icon: '🧘',
    metrics: ['duration', 'calories'],
    category: 'flexibility',
  },
  {
    id: 'pilates',
    name: 'Pilates',
    icon: '🤸',
    metrics: ['duration', 'calories'],
    category: 'flexibility',
  },
  {
    id: 'stretching',
    name: 'Stretching',
    icon: '🤸',
    metrics: ['duration'],
    category: 'flexibility',
  },

  // Sports
  {
    id: 'soccer',
    name: 'Soccer',
    icon: '⚽',
    metrics: ['duration', 'calories'],
    category: 'sports',
  },
  {
    id: 'basketball',
    name: 'Basketball',
    icon: '🏀',
    metrics: ['duration', 'calories'],
    category: 'sports',
  },
  {
    id: 'tennis',
    name: 'Tennis',
    icon: '🎾',
    metrics: ['duration', 'calories'],
    category: 'sports',
  },
  {
    id: 'volleyball',
    name: 'Volleyball',
    icon: '🏐',
    metrics: ['duration', 'calories'],
    category: 'sports',
  },
  {
    id: 'golf',
    name: 'Golf',
    icon: '⛳',
    metrics: ['duration', 'calories'],
    category: 'sports',
  },
  {
    id: 'skiing',
    name: 'Skiing',
    icon: '⛷️',
    metrics: ['duration', 'distance', 'calories'],
    category: 'sports',
  },
  {
    id: 'snowboarding',
    name: 'Snowboarding',
    icon: '🏂',
    metrics: ['duration', 'distance', 'calories'],
    category: 'sports',
  },
  {
    id: 'climbing',
    name: 'Rock Climbing',
    icon: '🧗',
    metrics: ['duration', 'calories'],
    category: 'sports',
  },

  // Other
  {
    id: 'dancing',
    name: 'Dancing',
    icon: '💃',
    metrics: ['duration', 'calories'],
    category: 'other',
  },
  {
    id: 'martial_arts',
    name: 'Martial Arts',
    icon: '🥋',
    metrics: ['duration', 'calories'],
    category: 'other',
  },
  {
    id: 'boxing',
    name: 'Boxing',
    icon: '🥊',
    metrics: ['duration', 'calories'],
    category: 'other',
  },
  {
    id: 'meditation',
    name: 'Meditation',
    icon: '🧘',
    metrics: ['duration'],
    category: 'other',
  },
  {
    id: 'other',
    name: 'Other Activity',
    icon: '🏃',
    metrics: ['duration', 'calories'],
    category: 'other',
  },
];

export const getActivityTypeById = (id: string): ActivityType | undefined => {
  return ACTIVITY_TYPES.find((type) => type.id === id);
};

export const getActivityTypesByCategory = (category: ActivityType['category']): ActivityType[] => {
  return ACTIVITY_TYPES.filter((type) => type.category === category);
};

export const ACTIVITY_CATEGORIES: Array<{ id: ActivityType['category']; name: string; icon: string }> = [
  { id: 'cardio', name: 'Cardio', icon: '🏃' },
  { id: 'strength', name: 'Strength', icon: '💪' },
  { id: 'flexibility', name: 'Flexibility', icon: '🧘' },
  { id: 'sports', name: 'Sports', icon: '⚽' },
  { id: 'other', name: 'Other', icon: '🏃' },
];
